import { Bell, Lock, Settings2, Shield, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";

const defaultSettings = {
  siteName: "JanShakti",
  siteDescription: "Political Accountability Platform",
  maintenanceMode: false,
  allowRegistrations: true,
  maxIssuesPerUser: 20,
  requirePhoneVerification: true,
  emailNotifications: true,
  autoModeration: true,
};

const tabs = [
  { id: "general", label: "General", icon: Settings2 },
  { id: "security", label: "Security", icon: Lock },
  { id: "moderation", label: "Moderation", icon: Shield },
  { id: "notifications", label: "Notifications", icon: Bell },
];

const SettingsPanel = () => {
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("general");

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get("/admin/settings");
        setSettings({ ...defaultSettings, ...(res.data || {}) });
      } catch (err) {
        console.error("Error fetching settings:", err);
        toast.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/settings", settings);
      setSettings({ ...defaultSettings, ...(res.data?.settings || settings) });
      toast.success("Settings saved successfully");
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#ff9933]" />
      </div>
    );
  }

  const toggleCards = [
    {
      key: "maintenanceMode",
      title: "Maintenance Mode",
      description: "Temporarily pause the platform during maintenance windows.",
      show: activeTab === "general",
    },
    {
      key: "allowRegistrations",
      title: "Allow Registrations",
      description: "Enable or disable new sign-ups on the platform.",
      show: activeTab === "general",
    },
    {
      key: "requirePhoneVerification",
      title: "Require Phone Verification",
      description: "Force every user to complete phone verification.",
      show: activeTab === "security",
    },
    {
      key: "autoModeration",
      title: "Auto Moderation",
      description: "Keep AI-assisted moderation active for newly raised issues.",
      show: activeTab === "moderation",
    },
    {
      key: "emailNotifications",
      title: "Email Notifications",
      description: "Send important platform events to the admin team.",
      show: activeTab === "notifications",
    },
  ].filter((item) => item.show);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Settings</h2>
          <p className="text-sm text-slate-500">Operational controls and admin-side platform configuration.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-6 py-3 font-medium text-white transition-colors hover:bg-slate-800 disabled:bg-slate-400"
        >
          <Sparkles className="h-4 w-4" />
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="overflow-hidden rounded-[2rem] border border-[#d7dfd4] bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-4">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? "bg-slate-900 text-white"
                      : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid gap-6 p-6 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-6">
            {activeTab === "general" && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Site Name</label>
                  <input
                    type="text"
                    value={settings.siteName}
                    onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm focus:border-[#ff9933] focus:outline-none focus:ring-2 focus:ring-[#ffe1b8]"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Site Description</label>
                  <textarea
                    rows={4}
                    value={settings.siteDescription}
                    onChange={(e) => setSettings({ ...settings, siteDescription: e.target.value })}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm focus:border-[#ff9933] focus:outline-none focus:ring-2 focus:ring-[#ffe1b8]"
                  />
                </div>
              </motion.div>
            )}

            {activeTab === "security" && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Max Issues Per User (15 min)</label>
                  <input
                    type="number"
                    value={settings.maxIssuesPerUser}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        maxIssuesPerUser: Number(e.target.value) || 0,
                      })
                    }
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm focus:border-[#ff9933] focus:outline-none focus:ring-2 focus:ring-[#ffe1b8]"
                  />
                </div>
              </motion.div>
            )}

            <div className="grid gap-4">
              {toggleCards.map((item) => (
                <div key={item.key} className="flex items-center justify-between rounded-[1.75rem] border border-slate-100 bg-[#fbfcfa] p-5">
                  <div className="pr-4">
                    <h4 className="font-semibold text-slate-900">{item.title}</h4>
                    <p className="mt-1 text-sm leading-6 text-slate-500">{item.description}</p>
                  </div>
                  <button
                    onClick={() => setSettings({ ...settings, [item.key]: !settings[item.key] })}
                    className={`relative h-7 w-14 rounded-full transition-colors ${
                      settings[item.key] ? "bg-[#138808]" : "bg-slate-300"
                    }`}
                  >
                    <span
                      className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-transform ${
                        settings[item.key] ? "left-8" : "left-1"
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-[1.75rem] border border-[#ffe1b8] bg-[#fff8ef] p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#b76500]">Current configuration</p>
              <div className="mt-4 space-y-3 text-sm text-slate-600">
                <p>Site: <span className="font-medium text-slate-900">{settings.siteName}</span></p>
                <p>Registrations: <span className="font-medium text-slate-900">{settings.allowRegistrations ? "Enabled" : "Disabled"}</span></p>
                <p>Moderation: <span className="font-medium text-slate-900">{settings.autoModeration ? "Automatic" : "Manual"}</span></p>
                <p>Phone verification: <span className="font-medium text-slate-900">{settings.requirePhoneVerification ? "Required" : "Optional"}</span></p>
              </div>
            </div>

            <div className="rounded-[1.75rem] border border-[#d3ead6] bg-[#f3fbf4] p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#106d06]">Admin note</p>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                These settings are connected to the live admin settings API. Save only when the configuration is ready to be applied platform-wide.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
